import {
  getCustomer as getCustomerFromAppointments,
  getIsPreserveOrderCustomer,
  getOrderHasAppointment,
} from '../register/order/selectors';
import { getCustomer } from '../register/customer/selectors';
import { resetValidation, validateSaleSuccess } from '../register/validated/actions';
import * as iosBridgeHelpers from '../iosBridge/helpers';
import * as webreaderActions from '../terminal/actions';
import { discoverReaders } from '../register/webReader/actions';
import * as webreaderConstants from '../terminal/constants';
import { showReceipt } from '../customer/receipts/actions';
import { showNotification } from '../common/helpers/notifications';
import { assignPayments } from '../register/payment/reducer/payments/actions';
import { getHasReaderPayment, getPayments } from '../register/payment/reducer/payments/selectors';
import { emptyCashPayment } from '../register/selectors';
import { processCharge, resetPayments, processWebreaderCharge } from '../register/payment/actions';
import { getTippingBarbers, getTips } from '../register/tipping/selectors';
import {
  cancelTipping,
  resetTippingBarbers,
  setAdjustments,
  setProcessing,
  setTippingBarbers,
} from '../register/tipping/actions';
import {
  openTipScreen,
  redirectToRegister,
  redirectToTipping,
  setPayingObject,
  webreaderPaymentSuccess,
} from '../main/payment/actions';
import { CHARGE, CHECKOUT } from '../register/sale/constants';
import { CANCEL_TIPPING, TIPPING_DONE } from '../register/tipping/constants';
import { PAYMENT_SUCCESS } from '../register/payment/constants';
import {
  CANCEL_PAYMENT,
  IOS_BRIDGE_PAYMENT_SUCCESS,
  OPEN_TIP_SCREEN,
  PAYMENT_FAILED,
  WEBREADER_PAYMENT_SUCCESS,
} from '../main/payment/constants';
import { READY_TO_PAY } from '../register/webReader/constants';
import { openChargeModal } from '../register/payment/reducer/chargeModal/actions';
import { isStripeReader } from '../main/selectors';
import * as iosBridgeActions from '../iosBridge/actions';
import * as iosBridgeConstants from '../iosBridge/constants';
import {
  checkoutCart,
  createCart,
  getChargeId,
  getTotal,
  resetSale,
  serializeOrder,
  serializeOrderForListening,
  serializeWebreaderOrder,
  shouldAssignEmptyCashPayment,
  shouldProcessCharge,
  storeGiftCardCodes,
  createWebreaderSale,
  getGcSuccessMsg,
  trackPaymentSuccess,
} from './helpers';
import { getTotalPromosAndDiscounts } from '../register/validated/selectors';
import { setCart } from '../cart/actions';
import { getCartId } from '../cart/selectors';
import { resetSoldGiftCards } from '../register/giftCards/actions';
import { getSoldGiftCards, getGiftCards } from '../register/giftCards/selectors';
import { showMessage } from '../listeningMode/messages/actions';
import { activeShopWebreader } from '../auth/selectors';
import { getIsProcessorMode } from '../register/webReader/selector';
import {
  setCard,
  setCustomer,
} from '../register/customer/actions';
import { t } from '../../modules/infra/i18n';

const chargeSale = (store, cartId) => checkoutCart(
  store,
  cartId,
  getTips(store.getState()),
);

const openProcessCharge = async (store, cartId) => {
  store.dispatch(resetSoldGiftCards());

  const shouldUseWebreader = activeShopWebreader(store.getState()) && getHasReaderPayment(store.getState());
  if (shouldUseWebreader) {
    const sale = await createWebreaderSale(store, serializeWebreaderOrder(store));
    store.dispatch(validateSaleSuccess(sale));

    /**
     * In some cases(for example, when terminal payment amount is 0),
     * we don't need to process charge on terminal, and it's marked as finished right after creating a sale
     */
    const payment = sale.payments.find(item => item.type === 'reader');
    const isFinishedSale = sale.state === 'finished' && payment && payment.captured;
    if (isFinishedSale) {
      store.dispatch(webreaderPaymentSuccess(sale));
      return;
    }

    store.dispatch(processWebreaderCharge(sale));
    return;
  }

  chargeSale(store, cartId)
    .then((response) => {
      if (!getHasReaderPayment(store.getState())) {
        store.dispatch(storeGiftCardCodes(response));
      }
      store.dispatch(validateSaleSuccess(response));
      store.dispatch(processCharge(response));
    })
    .catch(() => {
      store.dispatch(cancelTipping());
    });
};

const openTipping = async (store) => {
  const shouldUseWebreader = activeShopWebreader(store.getState()) && getHasReaderPayment(store.getState());
  if (shouldUseWebreader) {
    if (shouldProcessCharge(store.getState())) {
      store.dispatch(setProcessing(true));
      openProcessCharge(store, null);
      return;
    }

    store.dispatch(openTipScreen(serializeOrderForListening(store)));
    return;
  }

  if (shouldAssignEmptyCashPayment(store.getState())) {
    store.dispatch(assignPayments([...getPayments(store.getState()), emptyCashPayment()]));
  }

  const cart = await createCart(store, serializeOrder(store));
  store.dispatch(setCart(cart.id));
  if (shouldProcessCharge(store.getState())) {
    openProcessCharge(store, cart.id);
    return;
  }
  store.dispatch(openTipScreen(cart.id));
};

const handleCheckout = (store) => {
  store.dispatch(setPayingObject());

  const state = store.getState();
  if (isStripeReader(state) && getHasReaderPayment(state)) {
    if (activeShopWebreader(state)) {
      store.dispatch(webreaderActions.stripeConnectTokenAction('pay'));
      return;
    }

    store.dispatch(iosBridgeActions.stripeConnectTokenAction('pay'));
    return;
  }

  openTipping(store);
};

const setTipScreen = (store) => {
  store.dispatch(setTippingBarbers(getTippingBarbers(store.getState())));
  store.dispatch(setAdjustments(getTotalPromosAndDiscounts(store.getState())));
  store.dispatch(redirectToTipping());
};

const handleCancelTipping = (store) => {
  store.dispatch(setProcessing(false));
  store.dispatch(resetTippingBarbers());
  store.dispatch(resetPayments());
  store.dispatch(resetValidation());

  store.dispatch(redirectToRegister());

  return true;
};

const paymentFailed = (store) => {
  store.dispatch(cancelTipping());
};

const openChargeScreen = (store) => {
  const state = store.getState();
  // set customer from appointments if available
  let customer = getCustomerFromAppointments(state);
  if (!customer && getIsPreserveOrderCustomer(state)) {
    customer = getCustomer(state);
  }
  store.dispatch(setCustomer(customer || {}));
  store.dispatch(setCard(null));
  store.dispatch(resetTippingBarbers());
  store.dispatch(resetPayments());
  store.dispatch(setPayingObject({}));
  store.dispatch(openChargeModal());
};

const handleTippingDone = (store) => {
  const shouldUseWebreader = activeShopWebreader(store.getState()) && getHasReaderPayment(store.getState());
  if (shouldUseWebreader) {
    openProcessCharge(store, null);
    return;
  }

  openProcessCharge(store, getCartId(store.getState()));
};

const handlePaymentSuccess = (store, action) => {
  const state = store.getState();

  const successNotification = {
    title: t('common:notifications.success_title'),
    level: 'success',
    message: t('register:payment.success_notification.message'),
  };

  const sendGiftCardMessages = (dispatch) => {
    const soldGiftCards = getSoldGiftCards(state);
    const previousGiftCards = getGiftCards(state);
    soldGiftCards.forEach((giftCard) => {
      const isTopUp = previousGiftCards.some(item => item.id === giftCard.referenceId);
      const text = getGcSuccessMsg(giftCard, isTopUp);
      const message = {
        title: t('register:gift_card.sold_notification.title'),
        message: text,
      };

      dispatch(showMessage(message));
    });
  };

  trackPaymentSuccess(action.payload.payments, getOrderHasAppointment(state));

  store.dispatch(
    showReceipt(
      getChargeId(state, action),
      getTotal(state),
      getCustomer(state),
      getOrderHasAppointment(state),
    )
  );
  showNotification({ ...successNotification, position: 'tr' });
  store.dispatch(setProcessing(false));
  sendGiftCardMessages(store.dispatch);
  resetSale(store);
};

const middleware = store => next => async (action) => {
  switch (action.type) {
    case CHARGE:
      openChargeScreen(store);
      break;

    case CHECKOUT:
      handleCheckout(store);
      break;

    case iosBridgeConstants.STRIPE_CONNECT_TOKEN:
      iosBridgeHelpers.handleStripeConnect(store, action, () => openTipping(store));
      break;

    case webreaderConstants.STRIPE_CONNECT_TOKEN:
      store.dispatch(discoverReaders(action.strategy));
      break;

    case READY_TO_PAY:
      // don't redirect to tipping in case it's webview webreader processor mode
      if (!getIsProcessorMode(store.getState())) {
        openTipping(store);
      }

      break;

    case OPEN_TIP_SCREEN:
      setTipScreen(store);
      break;

    case CANCEL_PAYMENT:
    case CANCEL_TIPPING:
      handleCancelTipping(store);
      break;

    case PAYMENT_FAILED:
      paymentFailed(store);
      break;

    case TIPPING_DONE:
      handleTippingDone(store);
      break;

    case PAYMENT_SUCCESS:
    case IOS_BRIDGE_PAYMENT_SUCCESS:
    case WEBREADER_PAYMENT_SUCCESS:
      handlePaymentSuccess(store, action);
      break;

    default:
  }

  next(action);
};

export default middleware;
